import { Component, Host, Prop, Event, h, EventEmitter, State } from '@stencil/core';
import { WidgetClasses } from '../../types/props';
import { Project, Reaction } from '../../types/data';
import { DEFAULT_URL } from '../../utils/constants';
import { collectReaction, fetchProject } from '../../utils/api';
import clsx from 'clsx';
import { createGetClass } from '../../utils/classes';
import { formatTemplate } from '../../utils/strings';
import { formatShortNumber } from '../../utils/numbers';
import { Fetcher } from '../fetcher/fetcher';

@Component({
  tag: 'hr-widget',
  styleUrl: 'hr-widget.css',
  shadow: false,
})
export class HrWidget {

  /**
   * Token of your project
   */
  @Prop() token!: string;

  /**
   * Unique identifier of resource
   */
  @Prop() resource!: string;

  /**
   * Classes applied to widget elements. You can find them on [Happy React styling guide](https://happyreact.com/docs/configuration/classes)
   */
  @Prop() classes: string | WidgetClasses;

  /**
   * Url where should widget get data. Used when you have own Happy React instance
   */
  @Prop() url: string = DEFAULT_URL;

  private _isFirstReaction() {
    return !this.response.data.reacted?.id;
  }

  private _isUnlimitedReactions() {
    return this.response.data.preferences.allowMultiple && this.response.data.preferences.maxReactions === 0;
  }

  private _isLimitReactionsNotReached(reaction: Reaction) {
    return this.response.data.reacted.id === reaction.id &&
      this.response.data.preferences.allowMultiple &&
      this.response.data.reacted.count < this.response.data.preferences.maxReactions
  }

  private _isLimitReactionsReached() {
    return this.response.data.reacted.count >= this.response.data.preferences.maxReactions;
  }

  private _getAffiliateURL() {
    if (typeof window !== 'undefined' && 'location' in window) {
      return `https://happyreact.com/?utm_source=${window.location.origin}&utm_medium=widget&utm_campaign=footer`;
    }

    return `https://happyreact.com/?utm_source=feedback&utm_medium=widget&utm_campaign=footer`;
  }


  async collect(reaction: Reaction) {
    if (this.response.status !== 'success') {
      return;
    }

    const data = structuredClone(this.response.data);

    if (
      this._isFirstReaction() ||
      this._isUnlimitedReactions() ||
      this._isLimitReactionsNotReached(reaction)
    ) {
      const foundReaction = data.reactions.find((currentReaction) => currentReaction.id === reaction.id);

      if (!data.reacted.id) {
        data.reacted.id = reaction.id;
        data.reacted.count = 1;
      } else {
        data.reacted.count += 1;
      }

      foundReaction.count += 1;

      this.response = {
        status: 'success',
        data
      };

      const response = await collectReaction({
        token: this.token,
        resource: this.resource,
        reactionId: reaction.id,
        url: this.url,
        count: 1
      });

      this.onReactionHandler(reaction, { resources: response.resources });
    } else if (this._isLimitReactionsReached()) {
      this.onReactionsLimitReachedHandler();
    }
  }

  /**
   * Form element
   */
  form = document.querySelector('hr-form');

  /**
   * Event fired when user click reaction
   */
  @Event() reaction: EventEmitter<Reaction>;

  /**
   * onReaction event handler
   * @param reaction 
   */
  onReactionHandler(reaction: Reaction, data: { resources: string[] }) {
    this.reaction.emit(reaction);

    if (this.form) {
      this.form.dispatchEvent(new CustomEvent('show', {
        detail: {
          token: this.token,
          reactionId: reaction.id,
          url: this.url,
          resources: data.resources,
          count: reaction.count
        }
      }));
    }
  }

  /**
   * Event fired when user reach reactions limit
   */
  @Event() reactionsLimitReached: EventEmitter<Reaction>;

  /**
   * onReactionsLimitReached event handler
   */
  onReactionsLimitReachedHandler() {
    this.reactionsLimitReached.emit();
  }

  @State() response:
    | { status: 'loading'; data: null }
    | { status: 'success'; data: Project }
    | { status: 'error'; data: null } = {
      status: 'loading',
      data: null
    }

  componentWillLoad() {
    fetchProject({
      token: this.token,
      resource: this.resource,
      url: this.url
    })
      .then((response) => {
        this.response = {
          status: 'success',
          data: response
        };
      })
      .catch((cause) => {
        this.response = {
          status: 'error',
          data: cause.message
        };
      });
  }

  render() {
    const getClass = createGetClass(this.classes);

    return (
      <Host class={getClass('root')}>
        <div class={clsx('hr-container', getClass('container'))}>
          <Fetcher status={this.response.status}>
            <div class={clsx('hr-grid', getClass('grid'))}>
              {this.response.data?.reactions.map((reaction) => (
                <div class={clsx('hr-cell', getClass('cell'))} key={reaction.id}>
                  <button
                    onClick={() => this.collect(reaction)}
                    class={clsx('hr-reaction',
                      getClass('reaction'), {
                      [clsx('hr-reaction-active', getClass('reactionActive'))]: this.response.data.reacted.id === reaction.id
                    })}
                  >
                    <span class={clsx('hr-reaction-icon', getClass('reactionIcon'))}>{reaction.icon}</span>
                    {reaction.label && (
                      <span class={clsx('hr-reaction-text', getClass('reactionText'))}>
                        {formatTemplate({
                          template: reaction.label,
                          data: {
                            count: this.response.data.preferences.shortNumber
                              ? String(formatShortNumber(reaction.count))
                              : String(reaction.count)
                          }
                        })}
                      </span>
                    )}
                  </button>
                </div>
              ))}
            </div>
            <a
              href={this._getAffiliateURL()}
              rel="noopener"
              target="_blank"
              class="hr-powered-by"
              ref={(node) => {
                if (node) {
                  node.style.setProperty('display', !this.response.data?.preferences.whitelabel ? 'block' : 'none', 'important');
                }
              }}
            >
              Feedback widget by Happy React
            </a>
          </Fetcher>
        </div>
      </Host>
    );
  }

}
